/**
 * Tries to extract a numerical value in Lakhs from a price string.
 * This is a best-effort parser since prices are free-form text.
 * Examples:
 * "₹59.9 Lakhs" -> 59.9
 * "Starting from ₹15.20L" -> 15.20
 * "Starts at ₹2.50 Cr" -> 250
 * "Starts at ₹4,700/- Per sft" -> null (cannot easily determine total price without area)
 */
export const extractPriceInLakhs = (priceString) => {
  if (!priceString) return null;
  const str = priceString.toLowerCase();
  
  // If price is per sq.yd or sq.ft, we can't reliably filter by total budget
  if (str.includes('per sq') || str.includes('per sft')) {
    return null;
  }

  // Extract all numbers (including decimals)
  const match = str.match(/[\d,]+\.?\d*/);
  if (!match) return null;

  let number = parseFloat(match[0].replace(/,/g, ''));

  if (str.includes('cr') || str.includes('crore')) {
    number = number * 100; // Convert Crores to Lakhs
  } else if (str.includes('lakh') || str.includes('l') || str.includes('lac')) {
    // Already in Lakhs, or it's a raw number that we'll assume is in Lakhs if it's small enough
    // But we just leave it as is.
  } else {
    // If it's a huge number like 15000000, convert to lakhs
    if (number > 100000) {
      number = number / 100000;
    }
  }

  return number;
};

/**
 * Checks if a property price string matches a budget range option
 */
export const isPriceInBudget = (priceString, budgetOption) => {
  if (!budgetOption || budgetOption === 'All' || budgetOption === 'Any Budget') return true;

  const priceInLakhs = extractPriceInLakhs(priceString);
  
  // If we couldn't parse the price (e.g. it's "Contact for details" or "Per sft"),
  // we should arguably include it so we don't hide properties that might fit.
  if (priceInLakhs === null) return true;

  if (budgetOption === 'Under ₹50 Lakhs') {
    return priceInLakhs < 50;
  } else if (budgetOption === '₹50 Lakhs - ₹1 Crore') {
    return priceInLakhs >= 50 && priceInLakhs <= 100;
  } else if (budgetOption === '₹1 Crore - ₹2 Crores') {
    return priceInLakhs > 100 && priceInLakhs <= 200;
  } else if (budgetOption === 'Above ₹2 Crores') {
    return priceInLakhs > 200;
  }

  return true;
};
